import {useFormik} from "formik";
import {useEffect, useMemo} from "react";
import {getCategoriesDataTC} from "../../state/reducers/categories-reducer";
import {useSelector} from "react-redux";
import {AppRootStateType} from "../../state/store";
import {FilteredChannelParams} from "../../state/models/ChannelResponse";
import {ICategoriesResponseData} from "../../state/models/CategoriesResponse";
import {
    fetchBlackListChannelsTC, fetchFavouritesChannelsTC, setEngagementFilteredChannelsDataTC,
    setFilteredAllChannelsDataTC,
    setFilteredChannelsDataTC,
} from "../../state/reducers/channel-reducer";
import {useAppDispatch} from "../useAppDispatch.ts";
import {useModal} from "../../context/ModalContext.tsx";
import {
    getChannelFilters,
    getEngagementChannelFilters,
    getParamsBlackListChannels, getParamsFavouritesChannels,
    getParamsOfAllChannels,
} from "../../utils/Utils.ts";
import {useModalClose} from "../modal/useCloseModal.ts";
import {useTranslation} from "react-i18next";

const useSearchChannelPage = ({searchType, modalId}: {
    searchType: "allChannels" | "createCampaign" | "blacklist" | "engagementCampaignSetting" | "favourites",
    modalId: string
}) => {
    const dispatch = useAppDispatch();
    const closeManually = useModalClose(modalId);
    const {openModal} = useModal();
    const {t} = useTranslation();

    const openAvailableModal = () => {
        openModal('AVAILABLE_CHANNELS', {
            isOpen: true,
            onConfirm: () => {
            },
        })
    };
    const isLoading = useSelector(
        (state: AppRootStateType) => state.categories.isLoading
    );
    const categoriesData = useSelector<AppRootStateType, ICategoriesResponseData[] | null>((state) => state.categories.data);


    const defaultValues: FilteredChannelParams = useMemo(
        () => ({
            query: "",
            subscribers: "",
            languages: "",
            countries: "",
            categories: [],
            page: 0,
            limit: 0,
        }),
        []
    );

    const localStorageValues: Partial<FilteredChannelParams> = useMemo(() => {
        switch (searchType) {
            case "allChannels":
                return getParamsOfAllChannels() || {};
            case "createCampaign":
                return getChannelFilters() || {tp: true};
            case "blacklist":
                return getParamsBlackListChannels() || {};
            case "engagementCampaignSetting":
                return getEngagementChannelFilters() || {tp: true};
            case "favourites":
                return getParamsFavouritesChannels() || {};
            default:
                return {};
        }
    }, [searchType]);

    const initialValues: FilteredChannelParams = useMemo(() => {
        // Извлекаем числовые значения из локалсторедж
        const min = localStorageValues.min_subscribers;
        const max = localStorageValues.max_subscribers;

        // Формируем subscribers строку, если есть оба числа
        const subscribersStr =
            typeof min === "number" && typeof max === "number"
                ? `${min}/${max}`
                : "";

        return {
            ...defaultValues,
            ...localStorageValues,
            subscribers: subscribersStr, // перезаписываем subscribers в нужном формате
        };
    }, [defaultValues, localStorageValues]);

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const cleanValues = Object.fromEntries(
                Object.entries(values).filter(([key, value]) => {
                    if (key === "subscribers") return false; // исключаем subscribers из чистого объекта
                    if (Array.isArray(value)) return value.length > 0;
                    if (typeof value === "string") return value.trim() !== "";
                    return value !== 0 && value !== null && value !== undefined;
                })
            );

            // Парсим subscribers в числа
            const parts = values.subscribers ? values.subscribers.split("/") : [];
            const nums = parts.map(part => Number(part));

            // Собираем итоговый объект без subscribers, с min и max
            const payload: FilteredChannelParams = {
                ...cleanValues,
                min_subscribers: nums[0],
                // Добавляем max_subscribers только если есть валидное значение
                ...(nums[1] !== undefined && !isNaN(nums[1]) ? { max_subscribers: nums[1] } : {}),
            };

            try {
                switch (searchType) {
                    case "allChannels":
                        await dispatch(setFilteredAllChannelsDataTC(payload));
                        break;
                    case "createCampaign":
                        await dispatch(setFilteredChannelsDataTC(payload, true));
                        break;
                    case "blacklist":
                        await dispatch(fetchBlackListChannelsTC(payload));
                        break;
                    case "engagementCampaignSetting":
                        await dispatch(setEngagementFilteredChannelsDataTC(payload, true));
                        break;
                    case "favourites":
                        await dispatch(fetchFavouritesChannelsTC(payload));
                        break;
                }
                closeManually()
            }
            catch (err) {
                console.log("Ошибка при search канала:", err);
            }
        },
    });


    useEffect(() => {
        if (!categoriesData) {
            dispatch(getCategoriesDataTC()).catch((err) =>
                console.log("Ошибка загрузки категорий:", err)
            );
        }
    }, [categoriesData, dispatch]);

    const resetForm = () => {
        formik.resetForm({
            values: {query: ""}
        });
        try {
            switch (searchType) {
                case "allChannels":
                    localStorage.removeItem('paramsOFAllChannels');
                    break;
                case "createCampaign":
                    localStorage.removeItem('channelFilters');
                    break;
                case "blacklist":
                    localStorage.removeItem('paramsOfBlacklistChannels');
                    break;
                case "engagementCampaignSetting":
                    localStorage.removeItem('engagementChannelFilters');
                    break;
                case "favourites":
                    localStorage.removeItem('paramsOfFavouritesChannels');
                    break;
            }
        }catch (e){
            console.log(e)
        }
    };

    const handleBackClick = () => {
        openAvailableModal()
    };

    return {
        isLoading,
        categoriesData,
        formik,
        resetForm,
        handleBackClick,
        closeManually,t
    };
};

export default useSearchChannelPage;
